import { useEffect, useRef, useState } from "react";
import { Col, Row, Input, Form, Checkbox, Button } from "antd";
import { BASE_COLORS } from "config/theme/constants";
import { BaseButtonsForm } from "components/common/forms/BaseButtonsForm";
import PhoneInputComponent from "components/common/PhoneInputComponent";
import { notificationController } from "components/common/notificationController";
import { messageStrings, userString } from "config/site.string";
import SpinLoader from "components/Loader/SpinLoader";
import { useDispatch } from "react-redux";
import { addUpdateCustomerRequest } from "redux/ltlCustomers/actions";
import { config } from "config/site.config";
import "react-phone-input-2/lib/style.css";

const { TextArea } = Input;

interface IAddUpdateUserForm {
  form: any;
  handleOk: any;
  isEdit: boolean;
  onClose: (isSuccess?: boolean) => void;
  handleCancel: () => void;
  selectedRecord: any;
}

export const EditLtlCustomers = ({
  form,
  isEdit,
  onClose,
  selectedRecord,
}: IAddUpdateUserForm) => {
  const dispatch = useDispatch();
  const initializedCountry = useRef(false);
  const [mobilePhoneData, setMobilePhoneData] = useState<any>({
    phoneValue: "",
  });
  const [pending] = useState<boolean>(false);
  const currentUser = JSON.parse(
    localStorage.getItem("loggedInUser") || "null"
  );
  
  useEffect(() => {
    if (!initializedCountry.current) {
      initializedCountry.current = true;
      const mobilePhone = `${selectedRecord?.phoneExt?.toString()}${selectedRecord?.phone?.toString()}`;
      if (!isEdit) {
        form.resetFields();
      } else {
        setMobilePhoneData({
          ...mobilePhoneData,
          phoneNumber: selectedRecord?.phone?.toString(),
          phoneExt: selectedRecord?.phoneExt?.toString(),
          phoneValue: mobilePhone?.toString(),
        });
      }
    }
  }, []);

  const addCustomerCallback = (type: string, message: string) => {
    if (type === "Success") {
      notificationController.success({
        message: "Success",
        description: message
          ? message
          : `${
              isEdit
                ? `Customer updated successfully`
                : `Customer added successfully`
            }`,
        placement: "topRight",
      });
      form.resetFields();
      onClose(true);
    } else {
      notificationController.error({
        message: "Error",
        description: message ? message : "Sorry an error occurred",
        placement: "topRight",
      });
    }
  };

  const handleSubmit = (values: any) => {
    const customerValues: any = {
      email: values.email,
      deliveryApptRequired:
        values.deliveryApptRequired === undefined
          ? false
          : values.deliveryApptRequired,
      id: isEdit ? selectedRecord?.id : null,
      companyName: values.companyName,
      wtCode: values.wtCode,
      address1: values.address1,
      address2: values.address2,
      city: values.city,
      state: values.state,
      zip: values.zip,
      workingHours: values.workingHours,
      phone:
        mobilePhoneData.phoneNumber === "" ? null : mobilePhoneData.phoneNumber,
      phoneExt:
        mobilePhoneData.phoneExt === "" ? null : mobilePhoneData.phoneExt,
      specialInstructions: values.specialInstructions,
      createdBy: currentUser?.userDetails?.userId,
    };
    dispatch(
      addUpdateCustomerRequest(
        config.addUpdateCustomer,
        customerValues,
        addCustomerCallback
      )
    );
  };

  const handleCancelButtonClick = () => {
    form.resetFields();
    onClose();
  };

  return (
    <>
      {pending ? (
        <SpinLoader />
      ) : (
        <BaseButtonsForm
          form={form}
          name="customer"
          layout="horizontal"
          style={{ padding: "12px 20px 0px" }}
          onFinish={handleSubmit}
          id="customer"
        >
          <Row gutter={{ xs: 10, md: 15, xl: 30 }}>
            <Col xs={24} md={24}>
              <Form.Item
                name="companyName"
                label="Customer Name"
                labelCol={{ style: { minWidth: "200px", textAlign: "left" } }}
                rules={[
                  { required: true, message: "Customer name is required" },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={24}>
              <Form.Item
                name="wtCode"
                label="Wt Code"
                labelCol={{ style: { minWidth: "200px", textAlign: "left" } }}
                rules={[{ required: true, message: "Wt code is required" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={24}>
              <Form.Item
                name="email"
                label="Email"
                labelCol={{ style: { minWidth: "200px", textAlign: "left" } }}
                rules={[
                  {
                    required: false,
                    message: userString.emailAddressRequired,
                  },
                  {
                    type: "email",
                    message: messageStrings.emailNotValid,
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={24}>
              <Form.Item
                name="phone"
                label="Phone"
                labelCol={{ style: { minWidth: "200px", textAlign: "left" } }}
              >
                <PhoneInputComponent
                  form={form}
                  phoneData={mobilePhoneData}
                  setPhoneData={setMobilePhoneData}
                  country="us"
                  countriesList={["ca", "mx", "us"]}
                  fieldName="phone"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={24}>
              <Form.Item
                name="address1"
                label="Address1"
                labelCol={{ style: { minWidth: "200px", textAlign: "left" } }}
                rules={[{ required: true, message: "Address1 is required" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="address2"
                label="Address2"
                labelCol={{ style: { minWidth: "200px", textAlign: "left" } }}
                rules={[{ required: false, message: "" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={24}>
              <Form.Item
                name="city"
                label="City"
                labelCol={{ style: { minWidth: "200px", textAlign: "left" } }}
                rules={[{ required: true, message: "City is required" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={24}>
              <Form.Item
                name="state"
                label="State"
                labelCol={{ style: { minWidth: "200px", textAlign: "left" } }}
                rules={[{ required: true, message: "State is required" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={24}>
              <Form.Item
                name="zip"
                label="Zip"
                labelCol={{ style: { minWidth: "200px", textAlign: "left" } }}
                rules={[{ required: true, message: "Zip is required" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={24}>
              <Form.Item
                name="workingHours"
                label="Hours"
                labelCol={{ style: { minWidth: "200px", textAlign: "left" } }}
                rules={[{ required: false, message: "" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={24}>
              <Form.Item
                name={"deliveryApptRequired"}
                label="Delivery Appt. Required"
                valuePropName="checked"
                labelCol={{ style: { minWidth: "200px", textAlign: "left" } }}
              >
                <Checkbox
                  name={"deliveryApptRequired"}
                  className="checkboxColorClass"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={24}>
              <Form.Item
                name="specialInstructions"
                label="Special Instructions"
                labelCol={{ style: { minWidth: "200px", textAlign: "left" } }}
                rules={[
                  {
                    max: 2000,
                    message: "Maximum 2000 characters can be entered",
                  },
                ]}
              >
                <TextArea
                  count={{
                    show: true,
                    max: 2000,
                  }}
                  //autoSize={{ maxRows: 8 }}
                  className="no-resize"
                  style={{ border: `1px solid ${BASE_COLORS.altolight}` }}
                />
              </Form.Item>
            </Col>
          </Row>
           <div style={{ textAlign: "right", marginTop: 10 }}>
            <Button type="primary" htmlType="submit" style={{ marginRight: 5 }}>
              SUBMIT
            </Button>
            <Button type="primary" onClick={handleCancelButtonClick}>
              CANCEL
            </Button>
          </div>
        </BaseButtonsForm>
      )}
    </>
  );
};

export default EditLtlCustomers;
